import { range, sortBy, uniqBy } from 'lodash-es'
import { DateTime } from 'luxon'
import { useCallback, useMemo, useState } from 'react'

import { MONTHS } from '@/utils/constants'
import countries from '@/utils/countries'

import RPC from '@/client/rpc'
import { type CollegeItem, MAJORS } from '@/client/services/college'

import type { FormInputType, FormValues, Options } from '@/components/UI/Form/types'

const START_YEAR = 1900
const END_YEAR = DateTime.local().get('year') + 10
const DEFAULT_LIMIT = 100
export const COLLEGE_NOT_ON_LIST_OPTION_VALUE = 'college-not-on-list'
export const COLLEGE_NOT_ON_LIST_OPTION_LABEL = '[My college is not on this list]'
export const COLLEGE_NOT_ON_LIST_OPTION = {
  label: COLLEGE_NOT_ON_LIST_OPTION_LABEL,
  value: COLLEGE_NOT_ON_LIST_OPTION_VALUE,
}

export default function useCollegeInfoFormInputs(): {
  college: CollegeItem | null
  inputs: FormInputType[]
  collegeOtherDetails: { name: string; country: string } | null
} {
  const [colleges, setColleges] = useState<CollegeItem[]>([])
  const [collegeName, setCollegeName] = useState<string | null>(null)
  const [collegeNameOther, setCollegeNameOther] = useState<string | null>(null)
  const [collegeCountryOther, setCollegeCountryOther] = useState<string | null>(null)

  const loadColleges = useCallback(async (query: string) => {
    const newColleges = await RPC.getFilteredColleges(
      {
        query,
        limit: DEFAULT_LIMIT,
      },
      { disableProgressBar: true }
    )

    setColleges(oldColleges =>
      sortBy(
        uniqBy(oldColleges.concat(newColleges), c => c.name),
        c => c.name
      )
    )

    const options: Options = newColleges.map(c => ({ label: c.name, value: c.name }))
    return [COLLEGE_NOT_ON_LIST_OPTION, ...options]
  }, [])

  const inputs: FormInputType[] = useMemo(
    () => [
      {
        label: 'College Name',
        sublabel: `If you cannot find your college, please select the "${COLLEGE_NOT_ON_LIST_OPTION_LABEL}" option.`,
        type: 'select-async',
        name: 'name',
        loadOptions: loadColleges,
        onChange: setCollegeName,
        required: true,
        size: 6,
      },
      {
        label: 'What is the name of your college?',
        type: 'text',
        name: 'nameOther',
        dependencies: {
          keys: ['name'],
          callback(values: FormValues): boolean {
            return values.name === COLLEGE_NOT_ON_LIST_OPTION_VALUE
          },
        },
        onChange: setCollegeNameOther,
        required: true,
        size: 6,
      },
      {
        label: 'What country is your college in?',
        type: 'select',
        name: 'countryOther',
        options: countries.map(item => ({ value: item.code, label: item.name })),
        dependencies: {
          keys: ['name'],
          callback(values: FormValues): boolean {
            return values.name === COLLEGE_NOT_ON_LIST_OPTION_VALUE
          },
        },
        onChange: setCollegeCountryOther,
        required: true,
        size: 6,
      },
      {
        label: 'College Major',
        type: 'select',
        name: 'major',
        options: MAJORS.map(major => ({ label: major, value: major })),
        required: true,
        size: 6,
      },
      {
        label: 'Graduation Month?',
        type: 'select',
        name: 'graduationMonth',
        options: MONTHS.map((month, index) => ({ label: month, value: `${index + 1}` })),
        required: true,
        size: 3,
      },
      {
        label: 'Graduation Year?',
        type: 'select',
        name: 'graduationYear',
        options: range(END_YEAR, START_YEAR, -1).map(year => ({ label: year, value: `${year}` })),
        required: true,
        size: 3,
      },
      {
        label: 'Would you like your college name and major to be visible to other users?',
        type: 'multi-checkbox',
        name: 'visible',
        options: [
          {
            label: 'Yes, I would like my college name and major to be visible to other users.',
            value: 'yes',
          },
        ],
        required: false,
        size: 6,
      },
    ],
    [loadColleges]
  )

  return {
    college:
      collegeName === COLLEGE_NOT_ON_LIST_OPTION_VALUE
        ? null
        : (colleges.find(c => c.name === collegeName) ?? null),
    inputs,
    collegeOtherDetails:
      collegeName === COLLEGE_NOT_ON_LIST_OPTION_VALUE &&
      collegeNameOther != null &&
      collegeCountryOther != null
        ? { name: collegeNameOther, country: collegeCountryOther }
        : null,
  }
}
